import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Pin, PinOff } from "lucide-react";

import type { MenuUrl } from "config/permissions";
import type { AppEntry } from "components/SidebarNew/appMenu";

export interface AppTileItem {
  app: AppEntry;
  url: MenuUrl;
  subtitle: string;
}

// Mã màu Tailwind (text-{màu}-{độ}) của app trên menu trái → nền ô ứng dụng
const TILE_HEX: Record<string, string> = {
  blue: "#3b82f6", cyan: "#06b6d4", green: "#22c55e", red: "#ef4444", orange: "#f97316", purple: "#a855f7",
  violet: "#8b5cf6", yellow: "#eab308", teal: "#14b8a6", stone: "#78716c", amber: "#f59e0b", rose: "#f43f5e", sky: "#0ea5e9",
};
const tileColor = (app: AppEntry) => TILE_HEX[app.color.split("-")[1]] ?? "#64748b";

const CHAT_APP_NAME = "Chat";
const MAX_COLS = 6;

// Kích thước ô theo bề rộng khung (không theo màn hình — cột giữa trang chủ hẹp hơn màn hình)
const tileSize = (width: number) => (width < 640 ? { tile: 88, gap: 12, rows: 3 } : { tile: 112, gap: 24, rows: 2 });
const rowWidth = (cols: number, tile: number, gap: number) => cols * tile + (cols - 1) * gap;

/** Ghim / bỏ ghim app (theo tên app) */
export interface PinControl {
  isPinned: (name: string) => boolean;
  toggle: (name: string) => void;
}

/** Đưa app `name` tới vị trí `to` trong danh sách đang hiện */
export type MoveApp = (name: string, to: number) => void;

function TileFace({ item }: { item: AppTileItem }) {
  const { app, subtitle } = item;
  const Icon = app.icon;
  return (
    <>
      <div
        className="w-12 sm:w-14 md:w-16 h-12 sm:h-14 md:h-16 rounded-2xl flex items-center justify-center mb-2 md:mb-3 shadow-lg transition-all relative group-hover:shadow-xl group-hover:scale-110"
        style={{ backgroundColor: tileColor(app) }}
      >
        <Icon className="h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7 text-white" />
        {app.name === CHAT_APP_NAME && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold">
            1
          </span>
        )}
      </div>
      <span className="text-gray-200 font-medium text-xs sm:text-sm text-center line-clamp-2">{app.name}</span>
      <span className="w-full px-1 text-gray-500 text-[9px] sm:text-[10px] text-center line-clamp-1">{subtitle}</span>
    </>
  );
}

function AppTile({ item, tile, pin }: { item: AppTileItem; tile: number; pin?: PinControl }) {
  const { app, url, subtitle } = item;
  const pinned = pin?.isPinned(app.name) ?? false;
  return (
    <div className="group relative" style={{ width: tile }}>
      {pin && (
        <button
          type="button"
          onClick={() => pin.toggle(app.name)}
          title={pinned ? `Bỏ ghim ${app.name}` : `Ghim ${app.name} lên đầu`}
          aria-label={pinned ? `Bỏ ghim ${app.name}` : `Ghim ${app.name}`}
          className="absolute right-1 top-0 z-10 flex h-6 w-6 items-center justify-center rounded-full border border-white/15 bg-slate-900/90 text-slate-300 opacity-0 transition hover:border-teal-400/60 hover:text-teal-300 focus:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
        >
          {pinned ? <PinOff className="h-3 w-3" /> : <Pin className="h-3 w-3" />}
        </button>
      )}
      <Link
        to={url}
        title={subtitle}
        draggable={false}
        className="app-icon-container flex w-full flex-col items-center rounded-xl py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400/60"
      >
        <TileFace item={item} />
      </Link>
    </div>
  );
}

function useWidth(ref: React.RefObject<HTMLElement>) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const ro = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return width;
}

/** Ô ở chế độ sắp xếp (điện thoại / bàn phím): bấm mũi tên để đổi vị trí; không mở app */
function ArrangeTile({ item, index, count, tile, onMove }: {
  item: AppTileItem;
  index: number;
  count: number;
  tile: number;
  onMove: MoveApp;
}) {
  const name = item.app.name;
  const arrow = "flex h-6 w-6 items-center justify-center rounded-full border border-white/15 bg-slate-900/80 text-slate-300 hover:border-teal-400/60 hover:text-teal-300 disabled:opacity-30 disabled:hover:border-white/15 disabled:hover:text-slate-300";
  return (
    <div style={{ width: tile }} className="flex flex-col items-center rounded-xl border border-dashed border-white/15 py-1">
      <TileFace item={item} />
      <div className="mt-1.5 flex items-center gap-1.5">
        <button type="button" className={arrow} disabled={index === 0} onClick={() => onMove(name, index - 1)} aria-label={`Đưa ${name} sang trái`}>
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
        <span className="w-4 text-center text-[10px] tabular-nums text-slate-500">{index + 1}</span>
        <button type="button" className={arrow} disabled={index === count - 1} onClick={() => onMove(name, index + 1)} aria-label={`Đưa ${name} sang phải`}>
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

/**
 * Lưới căn giữa, các hàng dài bằng nhau (7 → 4 + 3) — dùng cho app đã ghim, kết quả tìm kiếm và khi sắp xếp.
 * `onMove`: cho nhấn giữ chuột trên ô rồi kéo để đổi vị trí (các ô dịch chỗ ngay khi kéo, thả ra thì lưu).
 * `arranging`: chế độ sắp xếp, mỗi ô có mũi tên trái / phải và không mở app.
 */
export function AppGrid({ items, pin, onMove, arranging = false }: {
  items: AppTileItem[];
  pin?: PinControl;
  onMove?: MoveApp;
  arranging?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const width = useWidth(ref);
  const { tile, gap } = tileSize(width);
  const cols = items.length ? Math.ceil(items.length / Math.ceil(items.length / MAX_COLS)) : 1;

  // Kéo bằng sự kiện con trỏ (không dùng kéo thả HTML5): `order` là thứ tự xem trước trong lúc kéo
  const press = useRef<{ name: string; x: number; y: number } | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [order, setOrder] = useState<string[] | null>(null);
  const justDragged = useRef(false);
  const canSort = !!onMove && items.length > 1;
  const shown = order ? order.flatMap((n) => items.filter((x) => x.app.name === n)) : items;

  const onPointerDown = (e: React.PointerEvent) => {
    justDragged.current = false;
    if (!canSort || e.pointerType !== "mouse" || e.button !== 0) return;
    const name = (e.target as HTMLElement).closest<HTMLElement>("[data-tile]")?.dataset.tile;
    press.current = name ? { name, x: e.clientX, y: e.clientY } : null;
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const p = press.current;
    if (!p) return;
    let current = order;
    if (!dragging) {
      if (Math.hypot(e.clientX - p.x, e.clientY - p.y) < 6) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      setDragging(p.name);
      current = items.map((x) => x.app.name);
      setOrder(current);
    }
    const over = document.elementFromPoint(e.clientX, e.clientY)?.closest<HTMLElement>("[data-tile]")?.dataset.tile;
    if (!current || !over || over === p.name) return;
    const next = current.filter((n) => n !== p.name);
    next.splice(current.indexOf(over), 0, p.name);
    if (next.join("|") !== current.join("|")) setOrder(next);
  };
  const onPointerUp = () => {
    const p = press.current;
    press.current = null;
    if (!p || !dragging) return;
    if (order && onMove) onMove(p.name, order.indexOf(p.name));
    justDragged.current = true;
    setDragging(null);
    setOrder(null);
  };
  // Kéo xong thì không mở app dưới con trỏ
  const onClickCapture = (e: React.MouseEvent) => {
    if (!justDragged.current) return;
    justDragged.current = false;
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div ref={ref} className="w-full">
      <div
        className={`mx-auto flex flex-wrap justify-center ${canSort ? "select-none" : ""} ${dragging ? "cursor-grabbing" : ""}`}
        style={{ maxWidth: rowWidth(cols, tile, gap), columnGap: gap, rowGap: 20 }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onClickCapture={onClickCapture}
      >
        {shown.map((item, i) => (
          <div
            key={item.app.name}
            data-tile={item.app.name}
            title={canSort && !arranging ? "Nhấn giữ và kéo để đổi vị trí" : undefined}
            className={`rounded-xl transition-opacity ${canSort ? "cursor-grab" : ""} ${dragging === item.app.name ? "opacity-50 ring-2 ring-teal-400/60" : ""}`}
          >
            {arranging && onMove
              ? <ArrangeTile item={item} index={i} count={shown.length} tile={tile} onMove={onMove} />
              : <AppTile item={item} tile={tile} pin={pin} />}
          </div>
        ))}
      </div>
    </div>
  );
}
/**
 * Mọi ứng dụng xếp thành các trang (2 hàng; khung hẹp 3 hàng), kéo chuột / vuốt ngang để xem trang sau.
 * Thứ tự do trang chủ quyết định (app hay dùng đứng đầu → trang 1).
 */
export function AppSlider({ items, pin }: { items: AppTileItem[]; pin?: PinControl }) {
  const ref = useRef<HTMLDivElement>(null);
  const width = useWidth(ref);
  const { tile, gap, rows } = tileSize(width);
  const cols = Math.min(MAX_COLS, Math.max(1, Math.floor((width + gap) / (tile + gap))));
  const perPage = cols * rows;
  const pages: AppTileItem[][] = [];
  for (let i = 0; i < items.length; i += perPage) pages.push(items.slice(i, i + perPage));

  const [page, setPage] = useState(0);
  const [dragging, setDragging] = useState(false);
  const drag = useRef<{ x: number; left: number; dx: number } | null>(null);
  const justDragged = useRef(false);

  const goTo = (i: number) => {
    const el = ref.current;
    if (!el) return;
    const target = Math.max(0, Math.min(pages.length - 1, i));
    el.scrollTo({ left: target * el.clientWidth, behavior: "smooth" });
    setPage(target);
  };

  // Đổi kích thước khung hoặc số trang (ghim / bỏ ghim) → giữ đúng trang đang xem
  useEffect(() => {
    const el = ref.current;
    const target = Math.max(0, Math.min(page, pages.length - 1));
    if (el) el.scrollLeft = target * el.clientWidth;
    if (target !== page) setPage(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width, perPage, pages.length]);

  // Chuột: kéo để lật trang (cảm ứng dùng cuộn ngang sẵn có của trình duyệt)
  const onPointerDown = (e: React.PointerEvent) => {
    justDragged.current = false;
    if (e.pointerType !== "mouse" || e.button !== 0 || !ref.current) return;
    drag.current = { x: e.clientX, left: ref.current.scrollLeft, dx: 0 };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d || !ref.current) return;
    d.dx = e.clientX - d.x;
    if (!dragging && Math.abs(d.dx) > 5) setDragging(true);
    ref.current.scrollLeft = d.left - d.dx;
  };
  const endDrag = () => {
    const d = drag.current;
    drag.current = null;
    if (!d || Math.abs(d.dx) <= 5) return;
    justDragged.current = true;
    setDragging(false);
    goTo(page + (d.dx < -50 ? 1 : d.dx > 50 ? -1 : 0));
  };
  // Kéo xong thì không mở app dưới con trỏ
  const onClickCapture = (e: React.MouseEvent) => {
    if (!justDragged.current) return;
    justDragged.current = false;
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div className="w-full">
      <div
        ref={ref}
        onScroll={(e) => {
          if (dragging) return;
          const el = e.currentTarget;
          if (el.clientWidth) setPage(Math.round(el.scrollLeft / el.clientWidth));
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        onClickCapture={onClickCapture}
        onDragStart={(e) => e.preventDefault()}
        className={`flex w-full select-none overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${pages.length > 1 ? "cursor-grab" : ""} ${dragging ? "cursor-grabbing" : "snap-x snap-mandatory"}`}
      >
        {pages.map((items, i) => (
          <div key={i} className="w-full shrink-0 snap-start">
            <div className="mx-auto flex flex-wrap justify-center content-start" style={{ maxWidth: rowWidth(cols, tile, gap), columnGap: gap, rowGap: 20 }}>
              {items.map((item) => <AppTile key={item.app.name} item={item} tile={tile} pin={pin} />)}
            </div>
          </div>
        ))}
      </div>

      {pages.length > 1 && (
        <div className="mt-4 flex items-center justify-center gap-3 text-[10px] text-slate-500">
          <div className="flex items-center gap-1.5">
            {pages.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Trang ${i + 1}`}
                aria-current={i === page}
                onClick={() => goTo(i)}
                className={`h-1.5 rounded-full transition-all ${i === page ? "w-5 bg-teal-400" : "w-1.5 bg-white/25 hover:bg-white/50"}`}
              />
            ))}
          </div>
          <span className="hidden sm:inline">Kéo ngang để xem thêm</span>
        </div>
      )}
    </div>
  );
}
